// Bucket management from the admin console: create a bucket for a customer,
// and rename/retire one. Previously the console could only LIST buckets, so an
// operator had to ask the customer to do it or go into the database.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/client';
import { adminApi } from '../api/adminClient';
import { RETENTION_POLICIES } from '../api/types';
import type { RetentionPolicy } from '../api/types';
import { Field } from '../components/Field';
import { Button } from '../components/ui';
import { useToast } from '../components/Toast';
import { IconClose, IconPlus } from '../components/Icons';

export function AdminBucketTools({ tenantId, onChanged }: { tenantId: string; onChanged: () => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        <IconPlus width={14} height={14} />
        {t('admin.buckets.create')}
      </Button>
      {open ? (
        <CreateBucketDialog
          tenantId={tenantId}
          onClose={() => setOpen(false)}
          onCreated={() => {
            setOpen(false);
            onChanged();
          }}
        />
      ) : null}
    </>
  );
}

function CreateBucketDialog({
  tenantId,
  onClose,
  onCreated,
}: {
  tenantId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [name, setName] = useState('');
  const [policy, setPolicy] = useState<RetentionPolicy>('KEEP_ALL');
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError(t('common.required'));
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      await adminApi.adminCreateBucket(tenantId, name.trim(), policy);
      toast.success(t('admin.buckets.created', { name: name.trim() }));
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      setBusy(false);
    }
  };

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form className="dialog" onSubmit={submit} role="dialog" aria-modal="true" aria-labelledby="cb-title">
        <div className="row between">
          <h2 className="dialog__title" id="cb-title">
            {t('admin.buckets.createTitle')}
          </h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label={t('common.close')}>
            <IconClose width={16} height={16} />
          </button>
        </div>
        <div style={{ marginTop: 16 }}>
          <Field label={t('archives.nameLabel')} required error={error}>
            {(p) => <input className="input" {...p} value={name} autoFocus onChange={(e) => setName(e.target.value)} />}
          </Field>
          <Field label={t('retention.label')}>
            {(p) => (
              <select className="select" {...p} value={policy} onChange={(e) => setPolicy(e.target.value as RetentionPolicy)}>
                {RETENTION_POLICIES.map((rp) => (
                  <option key={rp} value={rp}>
                    {t(`retention.${rp}`)}
                  </option>
                ))}
              </select>
            )}
          </Field>
        </div>
        <div className="dialog__actions">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={busy}>
            {t('common.create')}
          </Button>
        </div>
      </form>
    </div>
  );
}

/** Rename an organisation. The id stays fixed — archives, audit rows and
 *  subscriptions all reference it. */
export function RenameTenantDialog({
  tenantId,
  current,
  onClose,
  onSaved,
}: {
  tenantId: string;
  current: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(current);
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError(t('common.required'));
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      await adminApi.adminRenameTenant(tenantId, name.trim());
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      setBusy(false);
    }
  };

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form className="dialog" onSubmit={submit} role="dialog" aria-modal="true" aria-labelledby="rn-title">
        <div className="row between">
          <h2 className="dialog__title" id="rn-title">
            {t('admin.customerDetail.renameTitle')}
          </h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label={t('common.close')}>
            <IconClose width={16} height={16} />
          </button>
        </div>
        <div style={{ marginTop: 16 }}>
          <Field label={t('common.name')} required error={error} hint={t('admin.customerDetail.idFixed', { id: tenantId })}>
            {(p) => <input className="input" {...p} value={name} autoFocus onChange={(e) => setName(e.target.value)} />}
          </Field>
        </div>
        <div className="dialog__actions">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={busy}>
            {t('common.save')}
          </Button>
        </div>
      </form>
    </div>
  );
}
